import { filterAgenciesByAppPermissions, filterAgenciesByPartnerPermissions } from '../utils/agencyPermissions';
import { getSitePublicId } from '../utils/apps';

export const checkAgencyPermissionsForPartnerConfig = (
    orgAgencies,
    agencyId,
    siteId,
    longname,
    platform,
    partnerName,
    appsList
) => {
    // If we don't have the required data, we can't check permissions
    // For agency configs, fail closed (return false) to be safe
    if (!orgAgencies || !agencyId || !appsList?.length) {
        return false;
    }

    // Find the app site to get its public_id
    let appSite = null;
    if (siteId) {
        // Try to find by siteId
        for (const app of appsList) {
            appSite = app.sites?.find(site => site.site_id === siteId);
            if (appSite) break;
        }
    }

    // Fallback to longname + platform if siteId not found
    if (!appSite && longname && platform) {
        for (const app of appsList) {
            appSite = app.sites?.find(site => site.longname === longname && site.platform === platform);
            if (appSite) break;
        }
    }

    // If we can't find the site, fail closed for agency configs (they shouldn't have access)
    if (!appSite) {
        return false;
    }

    const sitePublicId = getSitePublicId(appSite);

    // If no public_id, fail closed
    if (!sitePublicId) {
        return false;
    }

    // Check app site permissions
    const filteredAgenciesByApp = filterAgenciesByAppPermissions(orgAgencies, [appSite]);
    const agencyAppResult = filteredAgenciesByApp.find(({ agency }) => agency.agency_id === agencyId);
    // Fail closed: if agency not found or hasPermission is false, deny access
    const hasAppSitePermission = agencyAppResult?.hasPermission ?? false;

    // Check partner/source permissions
    // filterAgenciesByPartnerPermissions takes (agencies, partnerName, configuredPartners)
    // configuredPartners is optional and used for filtering, we can pass empty array
    const filteredAgenciesByPartner = filterAgenciesByPartnerPermissions(orgAgencies, partnerName);
    const agencyPartnerResult = filteredAgenciesByPartner.find(({ agency }) => agency.agency_id === agencyId);
    // Fail closed: if agency not found or hasPermission is false, deny access
    const hasPartnerPermission = agencyPartnerResult?.hasPermission ?? false;

    // Agency must have permissions to BOTH app site AND partner
    return hasAppSitePermission && hasPartnerPermission;
};

export const isViewMode = (
    partnerConfiguration,
    isAgency,
    readOnly,
    // Optional params for agency permissions check
    orgAgencies = null,
    appsList = null,
    partnerName = null
) => {
    // Original checks
    if (partnerConfiguration?.isCustom) {
        return true;
    }
    if (readOnly) {
        return true;
    }

    // Agency permissions check for advertisers viewing agency configs
    if (!isAgency && partnerConfiguration?.agencyId && orgAgencies && appsList?.length && partnerName) {
        const hasPermissions = checkAgencyPermissionsForPartnerConfig(
            orgAgencies,
            partnerConfiguration.agencyId,
            partnerConfiguration.siteId,
            partnerConfiguration.longname,
            partnerConfiguration.os || partnerConfiguration.platform,
            partnerName,
            appsList
        );
        if (!hasPermissions) {
            return true;
        }
    }

    return false;
};

export const isEditMode = (
    partnerConfiguration,
    isAgency,
    readOnly,
    orgAgencies = null,
    appsList = null,
    partnerName = null
) => {
    // Can't edit if no siteId or if in view mode
    if (!partnerConfiguration?.siteId) {
        return false;
    }
    // If isViewMode returns true (including agency permissions check), can't edit
    if (isViewMode(partnerConfiguration, isAgency, readOnly, orgAgencies, appsList, partnerName)) {
        return false;
    }
    return !readOnly;
};
